// Engagement cockpit Work tab (WORKSPACE_COCKPIT_SPEC.md §5.3, §9–§10; factory
// job 004 §4.8). The run header with a persistent Stop sits above the
// current-work region, which sits above the terminal. Tab, view, inspector and
// id live in the URL as plain strings; tab changes push history, selections
// replace it.

import { Link, useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { neutralPointer } from '../../features/discovery/collectorLabel.ts';
import { distinctEvidence } from '../../features/discovery/evidenceSummary.ts';
import { Ban, ChevronRight } from 'lucide-react';
import { formatDateTime, shortId } from '../../components/format.ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '../../components/Badge.tsx';
import { Banner } from '../../components/Banner.tsx';
import { Button } from '../../components/Button.tsx';
import { ModalDialog } from '../../components/Dialog.tsx';
import { EmptyState } from '../../components/EmptyState.tsx';
import { ReadOnlyNote } from '../../components/ReadOnlyNote.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { Tabs, Tab, TabList, TabPanel } from '../../components/Tabs.tsx';
import { TerminalLog } from '../../components/TerminalLog.tsx';
import { Toast } from '../../components/Toast.tsx';
import { DisabledTip } from '../../components/Tooltip.tsx';
import { announce } from '../../state/announcer.ts';
import {
  COMMAND_LIST,
  FREE_TEXT_ANSWER,
  RUN_STATUS_LABEL,
  TAB_LABEL,
  STEP_STATE_LABEL,
  boundEvents,
  clockTime,
  commandHelp,
  durationLabel,
  currentStep,
  finishedSentence,
  parseCommand,
  readCockpitSearch,
  runStatusShape,
  runStatusTone,
  stepCounts,
} from '../../features/engagement/model.ts';
import { Inspector } from '../../features/engagement/Inspector.tsx';
import { useCockpit } from '../../features/engagement/store.ts';
import type { RunStep } from '../../features/engagement/types.ts';
import { useRunPolling } from '../../features/engagement/useRunPolling.ts';
import { useWorkspaceFindings } from '../../features/findings/store.ts';
import { useSession } from '../../state/session.ts';
import { useCan } from '../../features/auth/permissions.ts';
import { readOwner } from '../../features/authorization/model.ts';
import { useReports } from '../../features/records/reportsStore.ts';
import { EvidenceExcerpt } from '../../components/EvidenceExcerpt.tsx';
import { StateLabel, TruthLabel } from '../../components/TruthLabel.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import styles from './EngagementPage.module.css';

const blockedFallback = 'The backend reports this run cannot start.';
const VIEWER_REASON = 'Viewers can’t run, stop or retry checks.';

const RUNNER_PHASE: Record<string, string> = {
  none: 'not created',
  creating: 'starting',
  running: 'running',
  failed: 'failed',
  removed: 'removed',
  unavailable: 'unavailable',
};

function stepMarker(state: RunStep['state'], index: number) {
  if (state === 'completed') return '✓';
  if (state === 'running') return '▶';
  if (state === 'failed' || state === 'blocked') return '✕';
  return String(index + 1);
}

function TimeText({ iso }: { iso: string }) {
  if (!iso) return <span className={styles.mono}>—</span>;
  return (
    <time className={styles.mono} dateTime={iso} title={iso}>
      {clockTime(iso)}
    </time>
  );
}

export function EngagementRoutePage() {
  const { engagementId } = useParams({ strict: false });
  return <EngagementPage engagementId={engagementId ?? ''} />;
}

export function EngagementPage({ engagementId }: { engagementId: string }) {
  const navigate = useNavigate();
  const router = useRouter();
  const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const search = useMemo(() => readCockpitSearch(rawSearch), [rawSearch]);
  const { tab, view, inspector, id } = search;

  const engagement = useCockpit((s) => s.engagement);
  const run = useCockpit((s) => s.run);
  const events = useCockpit((s) => s.events);
  const frozen = useCockpit((s) => s.frozen);
  const runState = useCockpit((s) => s.runState);
  const startPending = useCockpit((s) => s.startPending);
  const paused = useCockpit((s) => s.paused);
  const expanded = useCockpit((s) => s.expanded);
  const loading = useCockpit((s) => s.loading);
  const error = useCockpit((s) => s.error);
  const load = useCockpit((s) => s.load);
  const loadEarlier = useCockpit((s) => s.loadEarlier);
  const togglePause = useCockpit((s) => s.togglePause);
  const toggleExpanded = useCockpit((s) => s.toggleExpanded);
  const startRun = useCockpit((s) => s.startRun);
  const stopRun = useCockpit((s) => s.stopRun);
  const reset = useCockpit((s) => s.reset);
  const memory = useCockpit((s) => s.memory);
  const memoryError = useCockpit((s) => s.memoryError);
  const remember = useCockpit((s) => s.remember);

  const [toast, setToast] = useState<string | null>(null);
  const [retryOpen, setRetryOpen] = useState(false);
  const [commandFeedback, setCommandFeedback] = useState('');
  const stateFact = useRef<HTMLSpanElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [viewport, setViewport] = useState<{ w: number }>({ w: 1280 });

  const workspace = useSession((state) => state.workspace);
  const workspaces = useSession((state) => state.workspaces);
  const canWrite = useCan('workspace.write');
  const {
    attempt,
    findings,
    evidence,
    sourceRecords,
    findingEngagementLinks,
    loaded,
    loading: findingsLoading,
    stale: findingsStale,
    error: findingsError,
    loadLatestAttemptFindings,
  } = useWorkspaceFindings();
  const workspaceId = workspace?.id;
  const mandateEndsAt = workspace?.mandate?.endsAt;
  const readToken = workspace?.authorizationId ? readOwner(workspace.authorizationId) : '';
  const setupSessionId = workspace?.setupSessionId;
  useEffect(() => {
    if (workspaceId)
      void loadLatestAttemptFindings(workspaceId, { session: setupSessionId, token: readToken });
  }, [workspaceId, setupSessionId, readToken, loadLatestAttemptFindings]);
  const createReport = useReports((s) => s.createReport);
  const reports = useReports((s) => s.reports);
  const selectReport = useReports((s) => s.selectReport);
  const loadReports = useReports((s) => s.load);
  const [creatingReport, setCreatingReport] = useState(false);
  useEffect(() => {
    if (workspaceId) void loadReports(workspaceId);
  }, [workspaceId, loadReports]);

  // Context: every engagement is created from one finding (FLOW-05).
  const sourceLink = useMemo(
    () => findingEngagementLinks.find((item) => item.engagementId === engagementId) ?? null,
    [engagementId, findingEngagementLinks],
  );
  const targetFinding = useMemo(
    () => findings.find((finding) => finding.findingId === sourceLink?.findingId) ?? null,
    [sourceLink, findings],
  );

  const existingReport = targetFinding
    ? (reports.find(
        (report) =>
          report.workspaceId === workspaceId &&
          report.claims.some((claim) => claim.findingId === targetFinding.findingId),
      ) ?? null)
    : null;

  const relatedEvidence = useMemo(
    () =>
      distinctEvidence(
        evidence.filter((item) => targetFinding?.evidenceIds.includes(item.evidenceId)),
      ),
    [targetFinding, evidence],
  );

  // Another workspace's engagement never renders here, and its run isn't polled.
  const elsewhere = engagement !== null && engagement.workspaceId !== workspaceId;
  useRunPolling(engagement && !elsewhere ? engagement.id : undefined);

  useEffect(() => {
    void load(engagementId);
    // Local view state is cleared on leaving (also what the shell's Start over
    // relies on); the backend run is not stopped and its records stay.
    return () => reset();
  }, [engagementId, load, reset]);

  // Title: <View> · <Engagement name> · Traceira (§4.8).
  useEffect(() => {
    const name = elsewhere ? undefined : engagement?.name;
    document.title = name ? `${TAB_LABEL[tab]} · ${name} · Traceira` : `Work · Traceira`;
  }, [tab, engagement, elsewhere]);

  // A keyboard tab change moves focus to the view's Heading 1 (§4.8). After a mouse click
  // focus is not on the tab yet; moving it would make react-aria hand it back to the tab as
  // keyboard focus and draw the focus ring on a click.
  const firstTab = useRef(true);
  useEffect(() => {
    if (firstTab.current) {
      firstTab.current = false;
      return;
    }
    if (document.activeElement?.getAttribute('role') === 'tab') {
      heading.current?.focus({ preventScroll: true });
    }
  }, [tab]);

  // Viewport tracking for the inspector breakpoint (§4.8) and layout math.
  useEffect(() => {
    const update = () => setViewport({ w: window.innerWidth });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const counts = useMemo(() => stepCounts(run?.steps ?? []), [run]);
  const step = useMemo(() => currentStep(run?.steps ?? []), [run]);
  const status = run ? runState.status : 'not-started';
  const runUnavailable = !loading && !run;

  // The Stop button unmounts once the run leaves running/stop-requested; keep the
  // keyboard user's place on the state instead of dropping focus to the page.
  const previousStatus = useRef(status);
  useEffect(() => {
    const was = previousStatus.current;
    previousStatus.current = status;
    const wasActive = was === 'running' || was === 'stop-requested';
    const isActive = status === 'running' || status === 'stop-requested';
    if (
      wasActive &&
      !isActive &&
      (!document.activeElement || document.activeElement === document.body)
    ) {
      stateFact.current?.focus();
    }
  }, [status]);

  const setParams = useCallback(
    (
      patch: Partial<{ tab: string; view: string; inspector: string | null; id: string | null }>,
      push = false,
    ) => {
      const next = {
        tab: patch.tab ?? tab,
        view: patch.view ?? view,
        ...(patch.inspector === undefined
          ? inspector
            ? { inspector, id: id ?? '' }
            : {}
          : patch.inspector
            ? { inspector: patch.inspector, id: patch.id ?? '' }
            : {}),
      };
      return navigate({
        to: '/engagements/$engagementId',
        params: { engagementId },
        search: next,
        replace: !push,
      });
    },
    [navigate, engagementId, tab, view, inspector, id],
  );

  const closeInspector = useCallback(() => {
    const target = opener.current;
    opener.current = null;
    void setParams({ inspector: null, id: null }).then(() => {
      // Wait for the modal focus trap to unmount before restoring its opener.
      if (target?.isConnected) requestAnimationFrame(() => target.focus());
    });
  }, [setParams]);

  // --- Commands (§4.5) ---
  const runCommand = useCallback(
    (input: string): boolean | undefined => {
      const action = parseCommand(input);
      setCommandFeedback('');
      if (!canWrite && (action.kind === 'remember' || action.kind === 'stop')) {
        const reason = action.kind === 'remember' ? 'Viewers can’t save notes.' : VIEWER_REASON;
        announce(reason);
        setCommandFeedback(reason);
        return;
      }
      // Closing an inspector opened by a command returns to the control in use.
      const inUse = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      switch (action.kind) {
        case 'scope':
          opener.current = inUse;
          setParams({ inspector: 'scope', id: run?.scope.id ?? 'scope' });
          break;
        case 'plan':
          opener.current = inUse;
          setParams({ inspector: 'plan', id: run?.plan.id ?? 'plan' });
          break;
        case 'why':
          opener.current = inUse;
          setParams({ inspector: 'check', id: action.stepId });
          break;
        case 'evidence':
          opener.current = inUse;
          setParams({ inspector: 'evidence', id: action.evidenceId });
          break;
        case 'context':
          setParams({ tab: 'context' }, true);
          announce('Context tab opened.');
          break;
        case 'remember':
          if (engagement) {
            void remember(engagement.id, action.text).then((saved) =>
              setCommandFeedback(saved ? 'Saved to memory.' : ''),
            );
          }
          break;
        case 'report':
          setParams({ tab: 'report' }, true);
          announce('Report tab opened.');
          break;
        case 'stop':
          if (engagement) {
            if (runState.status === 'running') setToast('Stop requested.');
            void stopRun(engagement.id);
          }
          break;
        case 'help':
          announce(`Supported commands: ${COMMAND_LIST}`);
          setCommandFeedback(commandHelp());
          break;
        case 'unknown':
          announce(
            `“${input.trim()}” is not a supported command. The supported commands are ${COMMAND_LIST}.`,
          );
          setCommandFeedback(
            `“${input.trim()}” is not a supported command. Supported: ${COMMAND_LIST}.`,
          );
          // false keeps the typed text in the footer field.
          return false;
        case 'free-text':
          announce(FREE_TEXT_ANSWER);
          setCommandFeedback(FREE_TEXT_ANSWER);
          break;
      }
    },
    [setParams, stopRun, remember, engagement, run, runState.status, canWrite],
  );

  if (loading && !engagement) {
    return (
      <div className={styles.page}>
        <Skeleton label="Loading the engagement and its run view…" />
      </div>
    );
  }

  if (!engagement) {
    return (
      <section className={styles.missing} aria-labelledby="missing-title">
        <h1 id="missing-title" tabIndex={-1}>
          This engagement isn’t available from the backend yet.
        </h1>
        <p>{error ? error.message : 'The backend did not return this engagement.'}</p>
        <p>
          <Link to="/engagements" className={styles.backLink}>
            Back to Engagements
          </Link>
        </p>
      </section>
    );
  }

  if (elsewhere) {
    const home = workspaces.find((ws) => ws.id === engagement.workspaceId);
    return (
      <div className={styles.page}>
        <EmptyState
          title={`This engagement is in ${home?.name ?? 'another workspace'}`}
          description="Engagements open only in their own workspace, so nothing from it is shown here."
          action={
            home
              ? {
                  label: 'Open it there',
                  onPress: () =>
                    void import('../../features/workspace/switchWorkspace.ts').then((m) =>
                      m.switchWorkspace(router, home.id, router.state.location.pathname),
                    ),
                }
              : {
                  label: 'Back to engagements',
                  onPress: () => void navigate({ to: '/engagements' }),
                }
          }
        />
      </div>
    );
  }

  const current = engagement;

  const inspectorMode: 'dock' | 'drawer' | 'full' | null =
    inspector && id ? (viewport.w >= 1440 ? 'dock' : viewport.w >= 1024 ? 'drawer' : 'full') : null;

  // While paused the list, its count and the total stay as they were at Pause.
  const shownEvents = frozen?.events ?? events;
  const bounded = boundEvents(shownEvents);
  const total = frozen?.total ?? run?.eventCount ?? bounded.events.length;
  const newEventCount = frozen ? events.length - frozen.events.length : 0;
  const truncated =
    bounded.events.length < total ? { shown: bounded.events.length, total } : undefined;

  const stopExplanation =
    'Stop asks the runner to stop the current run. Pause updates only freezes this display.';
  const requestedBy = runState.stopRequestedBy ?? run?.stopRequestedBy;
  const requestedAt = runState.stopRequestedAt ?? run?.stopRequestedAt;

  return (
    <div className={styles.page} data-tab={tab}>
      {/* Run header (§4.6): persistent Stop. Below 768px the action row is a
          direct child of the page, so it stays sticky while the log scrolls. */}
      <header className={styles.runHeader}>
        <div className={styles.runInfo}>
          <div className={styles.runIdentity}>
            <h1 ref={heading} tabIndex={-1} className={styles.heading} title={current.name}>
              {current.name}
            </h1>
            <p className={styles.subtitle}>
              {sourceLink ? (
                <span>
                  From finding{' '}
                  <Link
                    to="/findings/$findingId"
                    params={{ findingId: sourceLink.findingId }}
                    className={styles.findingRef}
                    title={targetFinding?.title ?? sourceLink.findingId}
                  >
                    {shortId(sourceLink.findingId)}
                  </Link>
                </span>
              ) : (
                <Link to="/engagements" className={styles.backLink}>
                  Engagements
                </Link>
              )}
              <span>
                scope <code>{(run?.scope.targets ?? [current.target]).join(', ')}</code>
              </span>
              {attempt?.authority?.letterRevision !== undefined && (
                <span>letter rev. {attempt.authority.letterRevision}</span>
              )}
              {mandateEndsAt && <span>window open until {formatDateTime(mandateEndsAt)}</span>}
            </p>
            <p className={styles.objective}>
              {targetFinding && (
                <StateLabel kind="severity" state={targetFinding.normalizedSeverity} compact />
              )}
              <span>{current.objective}</span>
            </p>
            {run && (
              <p className={styles.runMeta}>
                Attempt {run.attempt} · last confirmed <TimeText iso={run.observedAt} /> ·{' '}
                {run.runner?.kind === 'engagement-container'
                  ? `Engagement container · ${RUNNER_PHASE[run.runner.phase]}`
                  : run.runner?.kind === 'workspace'
                    ? 'Workspace runner'
                    : 'No dedicated container yet'}
              </p>
            )}
          </div>
          <div className={styles.runFacts}>
            {run && (
              <>
                <span className={styles.fact} ref={stateFact} tabIndex={-1}>
                  <span className={styles.factLabel}>Status</span>
                  <Badge
                    tone={runStatusTone(status)}
                    shape={runStatusShape(status)}
                    data-badge={status}
                  >
                    {RUN_STATUS_LABEL[status]}
                  </Badge>
                </span>
                <span className={styles.fact}>
                  <span className={styles.factLabel}>Steps</span>
                  <span className={styles.factValue}>
                    {counts.finished} of {counts.total} finished
                  </span>
                </span>
              </>
            )}
          </div>
          {status === 'stop-requested' && (
            <p className={styles.stopNote}>
              Stop requested
              {requestedBy ? ` by ${requestedBy}` : ''}
              {requestedAt ? ' at ' : ''}
              {requestedAt && <TimeText iso={requestedAt} />}. Work is not confirmed stopped; the
              last confirmed state is running until the runner answers.
            </p>
          )}
          <span id="stop-explanation" className="sr-only">
            {stopExplanation}
          </span>
          <span id="run-blocked-reason" className="sr-only">
            {run?.blockedReason ?? blockedFallback}
          </span>
        </div>
        {!canWrite ? (
          run && <ReadOnlyNote>{VIEWER_REASON}</ReadOnlyNote>
        ) : (
          <div className={styles.stopWrap}>
            {run && (status === 'running' || status === 'stop-requested') && (
              <Button
                variant="secondary"
                onPress={() => {
                  if (status === 'running') setToast('Stop requested.');
                  void stopRun(current.id);
                }}
                aria-describedby="stop-explanation"
                className={styles.stop}
              >
                <Ban size={16} aria-hidden="true" />
                {status === 'stop-requested' ? 'Stop requested…' : 'Stop'}
              </Button>
            )}
            {run &&
              status === 'not-started' &&
              (!run.canRun ? (
                <DisabledTip content={run.blockedReason ?? blockedFallback}>
                  <Button variant="primary" isDisabled aria-describedby="run-blocked-reason">
                    Run plan
                  </Button>
                </DisabledTip>
              ) : (
                <Button variant="primary" onPress={() => void startRun(current.id)}>
                  Run plan
                </Button>
              ))}
            {run &&
              (status === 'stopped' || status === 'partially-complete' || status === 'failed') && (
                <>
                  <Button
                    variant="secondary"
                    onPress={() => setRetryOpen(true)}
                    isDisabled={!run.canRun}
                    aria-describedby={run.canRun ? undefined : 'run-blocked-reason'}
                  >
                    Retry review
                  </Button>
                  {!run.canRun && (
                    <p className={styles.blockedNote}>{run.blockedReason ?? blockedFallback}</p>
                  )}
                </>
              )}
          </div>
        )}
      </header>

      {run && runState.stale && (
        <Banner tone="attention" icon="offline" title="Connection stale">
          <p className={styles.panelText}>
            Showing the last confirmed update (<TimeText iso={run.observedAt} />
            ). Traceira keeps retrying; nothing is replayed or invented.
          </p>
        </Banner>
      )}
      {error && engagement && (
        <Banner tone="attention" title="Last request had a problem">
          <p className={styles.panelText}>{error.message}</p>
        </Banner>
      )}
      {search.fallbackNote && (
        <p className={styles.fallbackNote} role="note">
          {search.fallbackNote}
        </p>
      )}
      {findingsError && tab !== 'work' && (
        <Banner tone="attention" title="Could not refresh linked records">
          {findingsError.message}
        </Banner>
      )}

      <Tabs selectedKey={tab} onSelectionChange={(key) => setParams({ tab: String(key) }, true)}>
        <TabList aria-label="Engagement sections">
          <Tab id="work">Work</Tab>
          <Tab id="steps">Steps</Tab>
          <Tab id="context">Context</Tab>
          <Tab id="memory">Memory</Tab>
          <Tab id="evidence">Evidence</Tab>
          <Tab id="report">Report</Tab>
        </TabList>

        <TabPanel id="work">
          <div
            className={[styles.workPane, inspectorMode === 'dock' ? styles.withDock : ''].join(' ')}
          >
            <div className={styles.workColumn}>
              {runUnavailable ? (
                <Banner tone="info" icon="warning" title="Running checks isn’t available yet">
                  <p className={styles.panelText}>
                    The backend does not record a run for this engagement, so there is no timeline
                    to show. Nothing is simulated.
                  </p>
                </Banner>
              ) : (
                <div className={styles.workStage} data-expanded={expanded || undefined}>
                  {!expanded && run && (
                    <section className={styles.planCard} aria-labelledby="plan-title">
                      <header className={styles.planHead}>
                        <h2 id="plan-title" className={styles.sectionTitle}>
                          Plan
                        </h2>
                        <span className={styles.planMeta}>
                          {run.steps.length} {run.steps.length === 1 ? 'step' : 'steps'} · revision{' '}
                          {run.plan.revision}
                        </span>
                      </header>
                      {run.steps.length > 0 ? (
                        <ol className={styles.planSteps}>
                          {run.steps.map((st, index) => (
                            <li key={st.id}>
                              <button
                                type="button"
                                className={styles.planStep}
                                data-state={st.state}
                                aria-current={st.id === step?.id ? 'step' : undefined}
                                title={`Inspect ${st.id}`}
                                onClick={(e) => {
                                  opener.current = e.currentTarget;
                                  setParams({ inspector: 'check', id: st.id });
                                }}
                              >
                                <span className={styles.stepMarker} aria-hidden="true">
                                  {stepMarker(st.state, index)}
                                </span>
                                <span className={styles.planQuestion}>{st.question}</span>
                                <span className={styles.planState}>
                                  {STEP_STATE_LABEL[st.state]}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className={styles.panelText}>This plan has no steps yet.</p>
                      )}
                      {!step && finishedSentence(status, counts) && (
                        <p className={styles.countSentence}>{finishedSentence(status, counts)}</p>
                      )}
                      {counts.skipped > 0 && (
                        <p className={styles.skipped}>
                          {counts.skipped} skipped · skipped steps are not counted as tested
                          coverage
                        </p>
                      )}
                    </section>
                  )}

                  <TerminalLog
                    variant="console"
                    title={`${current.target} · activity`}
                    status={
                      <>
                        {RUN_STATUS_LABEL[status]}
                        {step && status === 'running' && counts.total
                          ? ` · step ${run ? run.steps.indexOf(step) + 1 : 0} of ${counts.total}`
                          : counts.total
                            ? ` · ${counts.finished} of ${counts.total} finished`
                            : ''}
                      </>
                    }
                    statusTone={status === 'running' ? 'live' : undefined}
                    events={bounded.events}
                    lines={[]}
                    filter={view}
                    onFilterChange={(f) => setParams({ view: f })}
                    totalEvents={total}
                    copyContext={{ engagement: current.name, runId: run?.runId ?? '' }}
                    selectedEventId={inspector === 'event' ? id : null}
                    onSelectEvent={(eventId) => {
                      const row = document.querySelector<HTMLElement>(
                        `[data-event-id="${CSS.escape(eventId)}"]`,
                      );
                      opener.current = row;
                      setParams({ inspector: 'event', id: eventId });
                    }}
                    paused={paused}
                    newEventCount={newEventCount}
                    onTogglePause={togglePause}
                    expanded={expanded}
                    onToggleExpanded={toggleExpanded}
                    truncated={truncated}
                    onLoadEarlier={
                      bounded.dropped === 0 ? () => void loadEarlier(current.id) : undefined
                    }
                    commands={{ onSubmit: runCommand, disabled: !run, feedback: commandFeedback }}
                  />
                </div>
              )}
            </div>

            {inspectorMode && id && inspector && (
              <Inspector
                selection={{ kind: inspector, id }}
                run={run}
                events={events}
                mode={inspectorMode}
                onClose={closeInspector}
              />
            )}
          </div>
        </TabPanel>

        <TabPanel id="steps">
          <section className={styles.tabSection} aria-labelledby="steps-title">
            <header className={styles.tabHeader}>
              <div className={styles.tabIntro}>
                <h2 id="steps-title" className={styles.tabTitle}>
                  Steps
                </h2>
                <p className={styles.panelText}>
                  {run
                    ? `Plan revision ${run.plan.revision} · attempt ${run.attempt}. Each step is one question the run must answer.`
                    : 'The backend does not record a plan for this engagement yet.'}
                </p>
              </div>
              {run && counts.total > 0 && (
                <div className={styles.progress}>
                  <span className={styles.progressLabel}>
                    {counts.finished} of {counts.total} finished
                  </span>
                  <span className={styles.progressTrack} aria-hidden="true">
                    <span style={{ width: `${(counts.finished / counts.total) * 100}%` }} />
                  </span>
                </div>
              )}
            </header>
            {run && run.steps.length > 0 ? (
              <ol className={styles.timeline}>
                {run.steps.map((st, index) => (
                  <li key={st.id} className={styles.timelineItem} data-state={st.state}>
                    <span className={styles.stepMarker} aria-hidden="true">
                      {stepMarker(st.state, index)}
                    </span>
                    <div className={styles.timelineBody}>
                      <p className={styles.timelineQuestion}>{st.question}</p>
                      <p className={styles.timelineMeta}>
                        <code>{st.id}</code>
                        <Badge tone="neutral" data-badge={st.state}>
                          {STEP_STATE_LABEL[st.state]}
                        </Badge>
                        {st.startedAt && (
                          <span>
                            Started <TimeText iso={st.startedAt} />
                          </span>
                        )}
                        {st.finishedAt && (
                          <span>
                            Finished <TimeText iso={st.finishedAt} />
                          </span>
                        )}
                        {st.startedAt && st.finishedAt && (
                          <span>
                            took{' '}
                            {durationLabel(Date.parse(st.finishedAt) - Date.parse(st.startedAt))}
                          </span>
                        )}
                        <span>
                          {st.evidenceIds.length} evidence record
                          {st.evidenceIds.length === 1 ? '' : 's'}
                        </span>
                      </p>
                    </div>
                    <Button
                      variant="tertiary"
                      size="small"
                      onPress={() =>
                        setParams({ tab: 'work', inspector: 'check', id: st.id }, true)
                      }
                    >
                      Inspect {st.id}
                      <ChevronRight size={14} aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ol>
            ) : (
              run && <p className={styles.emptyNote}>This plan has no steps yet.</p>
            )}
            {counts.skipped > 0 && (
              <p className={styles.skipped}>
                {counts.skipped} skipped · skipped steps are not counted as tested coverage
              </p>
            )}
          </section>
        </TabPanel>

        <TabPanel id="context">
          <section className={styles.tabSection} aria-labelledby="context-title">
            <header className={styles.tabHeader}>
              <div className={styles.tabIntro}>
                <h2 id="context-title" className={styles.tabTitle}>
                  Context
                </h2>
                <p className={styles.panelText}>
                  What this engagement must establish, the finding it was created from, and the
                  scope it may touch.
                </p>
              </div>
            </header>

            <div className={styles.objectiveBlock}>
              <span className={styles.kicker}>Objective</span>
              <p className={styles.objectiveText}>{current.objective}</p>
            </div>

            <div className={styles.contextGrid}>
              {targetFinding ? (
                <article className={styles.sourceCard} aria-labelledby="source-finding-title">
                  <span className={styles.kicker}>Source finding</span>
                  <p className={styles.sourceLabels}>
                    <StateLabel kind="severity" state={targetFinding.normalizedSeverity} compact />
                    <TruthLabel
                      dimension="review"
                      state={targetFinding.analystReviewState}
                      compact
                    />
                    <code title={targetFinding.findingId}>{shortId(targetFinding.findingId)}</code>
                  </p>
                  <h3 id="source-finding-title" className={styles.sourceTitle}>
                    {targetFinding.title}
                  </h3>
                  <p className={styles.sourceText}>{targetFinding.description}</p>
                  <Link
                    to="/findings/$findingId"
                    params={{ findingId: targetFinding.findingId }}
                    className={buttonClass('secondary', 'small')}
                  >
                    View finding details
                  </Link>
                </article>
              ) : (
                <Banner tone="attention" title="Source finding not loaded">
                  <p className={styles.panelText}>
                    {sourceLink
                      ? `Created from finding ${sourceLink.findingId}, which is not in the latest discovery attempt.`
                      : 'Engagements are created from a finding, and no finding link for this engagement was returned.'}
                  </p>
                </Banner>
              )}

              <dl className={styles.factList}>
                <div>
                  <dt>Scope</dt>
                  <dd>
                    <code>{(run?.scope.targets ?? [current.target]).join(', ')}</code>
                  </dd>
                </div>
                <div>
                  <dt>Environment</dt>
                  <dd>{run?.scope.environment ?? current.environment}</dd>
                </div>
                {run && run.scope.exclusions.length > 0 && (
                  <div>
                    <dt>Exclusions</dt>
                    <dd>
                      <code>{run.scope.exclusions.join(', ')}</code>
                    </dd>
                  </div>
                )}
                {targetFinding && (targetFinding.affectedAssets ?? []).length > 0 && (
                  <div>
                    <dt>Affected assets</dt>
                    <dd>
                      <code>{(targetFinding.affectedAssets ?? []).join(', ')}</code>
                    </dd>
                  </div>
                )}
                {mandateEndsAt && (
                  <div>
                    <dt>Window</dt>
                    <dd>Open until {formatDateTime(mandateEndsAt)}</dd>
                  </div>
                )}
                <div>
                  <dt>Created</dt>
                  <dd>
                    <TimeText iso={sourceLink?.requestedAt ?? current.createdAt} />
                    {' · '}
                    {sourceLink?.requestedBy.displayName ?? current.owner}
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        </TabPanel>

        <TabPanel id="memory">
          <section className={styles.tabSection} aria-labelledby="memory-title">
            <header className={styles.tabHeader}>
              <div className={styles.tabIntro}>
                <h2 id="memory-title" className={styles.tabTitle}>
                  Memory
                </h2>
                <p className={styles.panelText}>
                  What this engagement has learned and kept, with who recorded it and when.
                </p>
              </div>
              {canWrite && (
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => setParams({ tab: 'work' }, true)}
                >
                  Add a note in the terminal
                </Button>
              )}
            </header>
            {memoryError && (
              <Banner tone="attention" title="Memory had a problem">
                <p className={styles.panelText}>{memoryError}</p>
              </Banner>
            )}
            {memory === null ? (
              !memoryError && (
                <Banner tone="info" icon="warning" title="Engagement memory isn’t available yet">
                  <p className={styles.panelText}>
                    The backend does not serve memory for this engagement, so nothing is shown and
                    nothing is simulated.
                  </p>
                </Banner>
              )
            ) : memory.length === 0 ? (
              <p className={styles.emptyNote}>
                Nothing remembered yet.
                {canWrite && (
                  <>
                    {' '}
                    Type <code>/remember NOTE</code> in the terminal to keep a note.
                  </>
                )}
              </p>
            ) : (
              <ol className={styles.noteList}>
                {memory.map((entry) => (
                  <li key={entry.id} className={styles.noteItem}>
                    <p className={styles.noteMeta}>
                      <TimeText iso={entry.at} />
                      <span>{entry.author}</span>
                      {entry.stepId && <code>{entry.stepId}</code>}
                    </p>
                    <p className={styles.noteText}>{entry.text}</p>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </TabPanel>

        <TabPanel id="evidence">
          <section className={styles.tabSection} aria-labelledby="evidence-title">
            <header className={styles.tabHeader}>
              <div className={styles.tabIntro}>
                <h2 id="evidence-title" className={styles.tabTitle}>
                  Supporting evidence
                </h2>
                <p className={styles.panelText}>
                  {relatedEvidence.length} source record{relatedEvidence.length === 1 ? '' : 's'}{' '}
                  cited by the linked finding, with supplied checksums where available.
                </p>
              </div>
              <Link to="/evidence" className={buttonClass('secondary', 'small')}>
                Open evidence library
              </Link>
            </header>

            {relatedEvidence.length > 0 ? (
              <div className={styles.evidenceGrid}>
                {relatedEvidence.map((ev) => {
                  const src = sourceRecords.find((s) => s.sourceRecordId === ev.sourceRecordId);
                  return (
                    <EvidenceExcerpt
                      key={ev.evidenceId}
                      evidenceId={ev.evidenceId}
                      kind={ev.kind}
                      capturedAt={ev.capturedAt}
                      sourceSystem={src?.sourceSystem ?? ev.sourcePointer}
                      toolVersion={src?.sourceVersion}
                      sha256={src?.sha256}
                      sourceClass="RAW_TOOL_OUTPUT"
                      content={
                        ev.summary ??
                        `Observation record for ${ev.kind} (${neutralPointer(ev.sourcePointer)})`
                      }
                      onInspect={() => {
                        void navigate({
                          to: '/evidence',
                          search: { inspector: 'evidence', id: ev.evidenceId },
                        });
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <p className={styles.emptyNote}>No evidence collected for this engagement yet.</p>
            )}
          </section>
        </TabPanel>

        <TabPanel id="report">
          <section className={styles.tabSection} aria-labelledby="report-title">
            <header className={styles.tabHeader}>
              <div className={styles.tabIntro}>
                <h2 id="report-title" className={styles.tabTitle}>
                  Report
                </h2>
                <p className={styles.panelText}>
                  Turn the linked finding and its cited evidence into a draft you can review and
                  export.
                </p>
              </div>
            </header>

            <div className={styles.reportGrid}>
              <dl className={styles.factList}>
                <div>
                  <dt>Finding</dt>
                  <dd className={styles.factInline}>
                    {targetFinding ? (
                      <>
                        <StateLabel
                          kind="severity"
                          state={targetFinding.normalizedSeverity}
                          compact
                        />
                        <span>{targetFinding.title}</span>
                      </>
                    ) : (
                      'Not loaded'
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Scope</dt>
                  <dd>
                    <code>{current.target}</code> · {current.environment} environment
                  </dd>
                </div>
                <div>
                  <dt>Cited evidence</dt>
                  <dd>
                    {relatedEvidence.length} record{relatedEvidence.length === 1 ? '' : 's'}
                  </dd>
                </div>
                <div>
                  <dt>Audience</dt>
                  <dd>Technical analyst</dd>
                </div>
              </dl>

              <div className={styles.reportAction}>
                {existingReport && (
                  <>
                    <p className={styles.panelText}>
                      Report <code>{shortId(existingReport.reportId)}</code> (revision r
                      {existingReport.revisionNumber}) was created{' '}
                      {formatDateTime(existingReport.createdAt)}.
                    </p>
                    <Button
                      variant="primary"
                      onPress={() => {
                        selectReport(existingReport.reportId);
                        void navigate({ to: '/reports' });
                      }}
                    >
                      Open report
                    </Button>
                  </>
                )}
                {canWrite && (
                  <Button
                    variant={existingReport ? 'secondary' : 'primary'}
                    isDisabled={
                      !targetFinding ||
                      !workspaceId ||
                      !loaded ||
                      findingsLoading ||
                      findingsStale ||
                      Boolean(findingsError) ||
                      creatingReport
                    }
                    onPress={() => {
                      if (!targetFinding || !workspaceId) return;
                      setCreatingReport(true);
                      createReport(workspaceId, {
                        audience: 'ANALYST',
                        findingIds: [targetFinding.findingId],
                        title: `Investigation report: ${current.name}`,
                      })
                        .then(() => void navigate({ to: '/reports' }))
                        .catch((err: unknown) =>
                          setToast(
                            err instanceof Error ? err.message : 'The report was not created.',
                          ),
                        )
                        .finally(() => setCreatingReport(false));
                    }}
                  >
                    {creatingReport
                      ? 'Creating report…'
                      : existingReport
                        ? 'Create a new report'
                        : 'Create report'}
                  </Button>
                )}
                <p className={styles.reportNote}>
                  Reports are saved to this workspace and download as a PDF from the report.
                </p>
              </div>
            </div>
          </section>
        </TabPanel>
      </Tabs>

      {run && (
        <ModalDialog
          title="Retry this review?"
          isOpen={retryOpen}
          onOpenChange={setRetryOpen}
          actions={(close) => (
            <>
              <Button variant="secondary" onPress={close}>
                Cancel
              </Button>
              <Button
                variant="primary"
                isDisabled={!run.canRun || startPending}
                onPress={() => {
                  setToast('Retry requested.');
                  void startRun(current.id);
                  close();
                }}
              >
                Start attempt {run.attempt + 1}
              </Button>
            </>
          )}
        >
          <p>
            {run.steps.filter((st) => ['failed', 'blocked', 'skipped'].includes(st.state)).length >
            0
              ? `Steps to run again: ${run.steps
                  .filter((st) => ['failed', 'blocked', 'skipped'].includes(st.state))
                  .map((st) => `${st.id} (${st.state})`)
                  .join(', ')}.`
              : 'No failed, blocked or skipped step is recorded; the whole plan runs again.'}
          </p>
          <p>
            Attempt {run.attempt} stays as recorded: its events and retained outputs remain visible.{' '}
            {counts.sentence}.
          </p>
          <p>
            Scope stays {run.scope.targets.join(', ')} in {run.scope.environment}.{' '}
            {run.canRun
              ? 'The backend reports this run can start now.'
              : (run.blockedReason ?? 'The backend reports this run cannot start.')}
          </p>
        </ModalDialog>
      )}

      {toast && (
        <div className={styles.toastWrap}>
          <Toast message={toast} onDismiss={() => setToast(null)} />
        </div>
      )}
    </div>
  );
}
